import { useEffect, useRef, useState } from 'react';

export const PROCESSING_POLL_MS = 8000;
export const PROCESSING_MAX_POLLS = 8;

// While the server rebuilds a member's history in the background, refetch the profile every
// 8 seconds, at most 8 times in a row. Polling stops on an error and once processing ends; a
// new window starts a fresh count. Returns `{ pollsExhausted }` so the page can change its copy.
const useProcessingPoll = ({ processing, errorKind, windowWeeks, refetch }) => {
  const pollCount = useRef(0);
  const pollInFlight = useRef(false);
  const mounted = useRef(true);
  // Bumped after each response (and on a window change) to re-arm the timer without stacking.
  const [pollTick, setPollTick] = useState(0);
  const [pollsExhausted, setPollsExhausted] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A different window starts a fresh count. Nothing to reset on the first render.
  const shownWindow = useRef(windowWeeks);
  useEffect(() => {
    if (shownWindow.current === windowWeeks) {
      return;
    }
    shownWindow.current = windowWeeks;
    pollCount.current = 0;
    setPollsExhausted(false);
    setPollTick((n) => n + 1);
  }, [windowWeeks]);

  useEffect(() => {
    if (!processing || errorKind) {
      pollCount.current = 0;
      setPollsExhausted(false);
      return undefined;
    }
    if (pollCount.current >= PROCESSING_MAX_POLLS) {
      setPollsExhausted(true);
      return undefined;
    }
    if (pollInFlight.current) {
      // The response of the poll in flight bumps `pollTick`, which re-arms the timer.
      return undefined;
    }
    const timer = setTimeout(async () => {
      pollCount.current += 1;
      pollInFlight.current = true;
      try {
        await refetch();
      } catch (err) {
        // A failed poll surfaces through the page's own refresh error.
      } finally {
        pollInFlight.current = false;
        if (mounted.current) {
          setPollTick((n) => n + 1);
        }
      }
    }, PROCESSING_POLL_MS);
    return () => clearTimeout(timer);
  }, [processing, errorKind, pollTick, refetch]);

  return { pollsExhausted };
};

export default useProcessingPoll;
